import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from "react";
import { DeletableChips } from "../../../../components/DeletableChips/DeletableChips";
import { SearchSelect } from "../../../../components/SearchSelect/SearchSelect";
import {
  CreatedOption,
  MethodsOfUseForm,
  OptionType,
} from "../../../../types/types";
import { ChipType } from "../../../../components/Chips/Chips";
import styled from "styled-components/macro";
import { Controller } from "react-hook-form";
import { RegionMultiSelectFieldRef } from "./util";

const InputWrapper = styled.div`
  width: 100%;
  max-width: 520px;
  margin-bottom: 10px;
`;

interface RegionMultiSelectFieldProps {
  methodsOfUseForm: MethodsOfUseForm;
  name: string;
  options: OptionType<string>[];
  placeholder: string;
  required: boolean;
  disabled?: boolean;
  defaultValues?: OptionType<string>[];
}

export const RegionMultiSelectField = forwardRef<
  RegionMultiSelectFieldRef,
  RegionMultiSelectFieldProps
>(function RegionMultiSelectField(
  {
    methodsOfUseForm,
    name,
    options: dropdownOptions,
    placeholder,
    required,
    disabled = false,
    defaultValues = [],
  },
  ref
) {
  const [chips, setChips] = useState<ChipType[]>(() =>
    defaultValues.map(({ label, value }) => ({ name: label, id: value }))
  );
  const { control, errors, formState, register, setValue } = methodsOfUseForm;

  // Memoize available options based on selected chips
  const availableOptions = useMemo(() => {
    const selectedLabels = new Set(chips.map((chip) => chip.name));
    return dropdownOptions.filter(
      (option) => !selectedLabels.has(option.label)
    );
  }, [dropdownOptions, chips]);

  // Memoize form value based on chips
  const formValue = useMemo(
    () => chips.map(({ id, name }) => ({ value: id, label: name })),
    [chips]
  );

  // Memoize disabled chips for display
  const displayChips = useMemo(
    () =>
      disabled ? chips.map((chip) => ({ ...chip, active: false })) : chips,
    [chips, disabled]
  );

  const clearChips = useCallback(() => {
    setChips([]);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      clearChips,
    }),
    [clearChips]
  );

  const handleMultiSelectChange = useCallback(
    (data: CreatedOption<string> | null) => {
      if (data) {
        setChips((previous) => [
          ...previous,
          { name: data.label, id: data.value },
        ]);
      }
    },
    []
  );

  const handleChipClick = useCallback((name: string) => {
    setChips((chips) => chips.filter((item) => item.name !== name));
  }, []);

  // Update form value when chips change
  useEffect(() => {
    setValue(name, formValue);
  }, [formValue, setValue, name]);

  // Register field with react-hook-form
  useEffect(() => {
    register({ name, required });
  }, [name, register, required]);

  // Clear chips when disabled
  useEffect(() => {
    if (disabled) {
      setChips([]);
    }
  }, [disabled]);

  return (
    <>
      <InputWrapper>
        <Controller
          name={name}
          control={control}
          render={(field) => (
            <SearchSelect
              name={name}
              options={availableOptions}
              errors={errors}
              formState={formState}
              placeholder={placeholder}
              value={null}
              isCreatable={false}
              onChange={(data: CreatedOption<string> | null) => {
                field.onChange([...formValue, data]);
                handleMultiSelectChange(data);
              }}
              disabled={disabled}
            />
          )}
        />
      </InputWrapper>
      <DeletableChips
        chips={displayChips}
        noneSelectedName={placeholder}
        showNoneSelectedMessage={!disabled}
        handleClick={handleChipClick}
      />
    </>
  );
});
